import { ApiService } from './api';
import { ApiResponse } from '../types/auth';
import {
  DriverGroupResponse,
  CreateDriverGroupRequest,
  UpdateDriverGroupRequest
} from '../types/driverGroups';
import { OrderAssignedDriverResponse } from '../types/orders';

class DriverGroupsService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async listGroups(): Promise<ApiResponse<DriverGroupResponse[]>> {
    try {
      const data = await this.apiService.get<DriverGroupResponse[]>('/admin/driver-groups');
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch driver groups',
      };
    }
  }

  async createGroup(request: CreateDriverGroupRequest): Promise<ApiResponse<DriverGroupResponse>> {
    try {
      const data = await this.apiService.post<DriverGroupResponse>('/admin/driver-groups', request);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create driver group',
      };
    }
  }

  async getGroup(groupId: number): Promise<ApiResponse<DriverGroupResponse>> {
    try {
      const data = await this.apiService.get<DriverGroupResponse>(`/admin/driver-groups/${groupId}`);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch driver group',
      };
    }
  }

  async updateGroup(groupId: number, request: UpdateDriverGroupRequest): Promise<ApiResponse<DriverGroupResponse>> {
    try {
      const data = await this.apiService.put<DriverGroupResponse>(`/admin/driver-groups/${groupId}`, request);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update driver group',
      };
    }
  }

  async deleteGroup(groupId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(`/admin/driver-groups/${groupId}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete driver group',
      };
    }
  }

  async listDriversInGroup(groupId: number): Promise<ApiResponse<OrderAssignedDriverResponse[]>> {
    try {
      const data = await this.apiService.get<OrderAssignedDriverResponse[]>(
        `/admin/driver-groups/${groupId}/drivers`
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch group drivers',
      };
    }
  }

  async addDriverToGroup(groupId: number, driverId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        `/admin/driver-groups/${groupId}/drivers/${driverId}`,
        {}
      );
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to add driver to group',
      };
    }
  }

  async removeDriverFromGroup(groupId: number, driverId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(
        `/admin/driver-groups/${groupId}/drivers/${driverId}`
      );
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to remove driver from group',
      };
    }
  }
}

export const driverGroupsService = new DriverGroupsService();
